import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Notification from '../Notification';
import { SrOnly } from '../SrOnly';
import { Text } from '../Text';
import {
  Group,
  List,
  ListOverflowHidden,
  ScrollButtonBtn,
  ScrollButtonEnd,
  ScrollButtonStart,
  Tab,
  Wrapper,
} from './wrapper';

export interface TabsTabProps {
  label: string;
  isActive?: boolean;
  notificationCount?: number;
  count?: string | number;
  icon?: ReactNode;
  onClick?: () => void;
  testId?: string;
}

interface TabsProps {
  tabs: TabsTabProps[][];
}

const Tabs = ({ tabs }: TabsProps) => {
  const { t } = useTranslation('common');
  const listRef = useRef<HTMLDivElement | null>(null);
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const scrollButtonDistance = 300;

  const updateScrollButtons = () => {
    if (!listRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = listRef.current;
    setShowStart(scrollLeft > 0);
    setShowEnd(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    const list = listRef.current;
    if (!list) return;
    list.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      list.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [tabs]);

  const handleScrollLeft = () => {
    if (listRef.current) {
      listRef.current.scrollTo({
        left: listRef.current.scrollLeft - scrollButtonDistance,
        behavior: 'smooth',
      });
      updateScrollButtons();
    }
  };

  const handleScrollRight = () => {
    if (listRef.current) {
      listRef.current.scrollTo({
        left: listRef.current.scrollLeft + scrollButtonDistance,
        behavior: 'smooth',
      });
      updateScrollButtons();
    }
  };

  return (
    <Wrapper>
      <ListOverflowHidden>
        <List ref={listRef}>
          {tabs.map((group, groupIndex) => (
            <Group key={`tab-group-${groupIndex}`}>
              {group.map((tab, tabIndex) => (
                <Tab
                  key={`${tab.label}-${tabIndex}`}
                  onClick={tab.onClick}
                  title={tab.label}
                  $isActive={tab.isActive}
                  data-testid={tab.testId}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.notificationCount ? (
                    <Notification size="small">{tab.notificationCount}</Notification>
                  ) : (
                    ''
                  )}
                  {tab.count !== undefined && tab.count !== null && (
                    <Text
                      $tone={!tab.isActive ? 'alt' : undefined}
                      $weight="regular"
                      $size="xsmall"
                    >
                      ({tab.count})
                    </Text>
                  )}
                </Tab>
              ))}
            </Group>
          ))}
        </List>
      </ListOverflowHidden>
      <ScrollButtonStart $isVisible={showStart}>
        <ScrollButtonBtn onClick={handleScrollLeft} title={t('scrollListToLeft', { ns: 'common' })}>
          <CaretLeftIcon size={16} aria-hidden="true" />
          <SrOnly>{t('scrollListToLeft', { ns: 'common' })}</SrOnly>
        </ScrollButtonBtn>
      </ScrollButtonStart>
      <ScrollButtonEnd $isVisible={showEnd}>
        <ScrollButtonBtn
          onClick={handleScrollRight}
          title={t('scrollListToRight', { ns: 'common' })}
        >
          <CaretRightIcon size={16} aria-hidden="true" />
          <SrOnly>{t('scrollListToRight', { ns: 'common' })}</SrOnly>
        </ScrollButtonBtn>
      </ScrollButtonEnd>
    </Wrapper>
  );
};

export default Tabs;
